/**
 * BudgetDetailPage - Detail view of a forecast budget
 *
 * Displays full details of a budget with categories, version history,
 * and export options. Allows direct editing of category amounts,
 * adding and removing categories.
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBudgetStore } from '../store/budgetStore';
import { useAccountStore } from '../store/accountStore';
import { useBilanStore } from '../store/bilanStore';
import { useCategoryStore } from '../store/categoryStore';
import { useAuthStore } from '../store/authStore';
import {
  BudgetCategoryTable,
  BudgetFormModal,
  BudgetDuplicateModal,
} from '../components/budgets';
import { UpdateBudgetRequest, DuplicateBudgetRequest, BudgetVersion, BudgetCategory, Category } from '@budgira/shared';
import { cn } from '../utils/cn';

interface CategoryEdit {
  categoryId: string;
  categoryName: string;
  baseType: 'debit' | 'credit';
  plannedAmount: number;
  notes?: string;
}

const BudgetDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();

  const {
    currentBudget,
    budgets,
    versionHistory,
    isLoadingDetail,
    isLoadingVersions,
    isSubmitting,
    error,
    fetchBudget,
    fetchBudgets,
    updateBudget,
    deleteBudget,
    duplicateBudget,
    fetchVersionHistory,
    restoreVersion,
    exportPdf,
    clearCurrentBudget,
    clearVersionHistory,
    clearError,
  } = useBudgetStore();

  const { accounts, fetchAccounts } = useAccountStore();
  const { statements, fetchStatements } = useBilanStore();
  const { flatCategories, fetchCategories } = useCategoryStore();
  const canManageBudgets = useAuthStore((state) => state.canManageBudgets());

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [isVersionsOpen, setIsVersionsOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [restoreConfirmVersion, setRestoreConfirmVersion] = useState<BudgetVersion | null>(null);
  
  // Editable state for categories
  const [editedCategories, setEditedCategories] = useState<Map<string, CategoryEdit>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Edit mode state - check URL param for initial state, but only if user has permission
  const [isEditMode, setIsEditMode] = useState(searchParams.get('edit') === 'true' && canManageBudgets);
  
  // Ensure edit mode is disabled if user loses permission (e.g., on workspace switch)
  useEffect(() => {
    if (isEditMode && !canManageBudgets) {
      setIsEditMode(false);
      setSearchParams({});
    }
  }, [canManageBudgets, isEditMode, setSearchParams]);

  useEffect(() => {
    if (id) {
      fetchBudget(id);
      fetchBudgets();
      fetchAccounts();
      fetchStatements();
      fetchCategories();
    }

    return () => {
      clearCurrentBudget();
      clearVersionHistory();
    };
  }, [id, fetchBudget, fetchBudgets, fetchAccounts, fetchStatements, fetchCategories, clearCurrentBudget, clearVersionHistory]);

  // Reset edited categories when budget changes
  useEffect(() => {
    if (currentBudget) {
      const initial = new Map<string, CategoryEdit>();
      currentBudget.categories.forEach((cat) => {
        const key = cat.categoryId || cat.id;
        initial.set(key, {
          categoryId: cat.categoryId || cat.id,
          categoryName: cat.categoryName,
          baseType: cat.baseType,
          plannedAmount: cat.plannedAmount,
          notes: cat.notes,
        });
      });
      setEditedCategories(initial);
      setHasChanges(false);
    }
  }, [currentBudget]);

  // Fetch version history when modal opens
  useEffect(() => {
    if (isVersionsOpen && id) {
      fetchVersionHistory(id);
    }
  }, [isVersionsOpen, id, fetchVersionHistory]);

  // Handle amount change
  const handleAmountChange = useCallback((categoryId: string, amount: number) => {
    setEditedCategories((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(categoryId);
      if (existing) {
        newMap.set(categoryId, { ...existing, plannedAmount: amount });
      }
      return newMap;
    });
    setHasChanges(true);
  }, []);

  // Handle notes change
  const handleNotesChange = useCallback((categoryId: string, notes: string) => {
    setEditedCategories((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(categoryId);
      if (existing) {
        newMap.set(categoryId, { ...existing, notes });
      }
      return newMap;
    });
    setHasChanges(true);
  }, []);

  // Handle add category
  const handleAddCategory = useCallback((category: Category) => {
    setEditedCategories((prev) => {
      const newMap = new Map(prev);
      newMap.set(category.id, {
        categoryId: category.id,
        categoryName: category.name,
        baseType: category.baseType,
        plannedAmount: 0,
        notes: undefined,
      });
      return newMap;
    });
    setHasChanges(true);
  }, []);

  // Handle remove category
  const handleRemoveCategory = useCallback((categoryId: string) => {
    setEditedCategories((prev) => {
      const newMap = new Map(prev);
      newMap.delete(categoryId);
      return newMap;
    });
    setHasChanges(true);
  }, []);

  // Save category changes
  const handleSaveCategories = async () => {
    if (!id || !currentBudget) return;
    
    setIsSaving(true);
    const categories = Array.from(editedCategories.values()).map((cat) => ({
      categoryId: cat.categoryId,
      plannedAmount: cat.plannedAmount,
      notes: cat.notes,
    }));

    const result = await updateBudget(id, {
      categories,
      changeReason: 'Mise à jour des montants prévisionnels',
    });

    setIsSaving(false);
    if (result) {
      setHasChanges(false);
      setIsEditMode(false);
      setSearchParams({}); // Clear edit param from URL
    }
  };

  // Discard changes
  const handleDiscardChanges = () => {
    if (currentBudget) {
      const initial = new Map<string, CategoryEdit>();
      currentBudget.categories.forEach((cat) => {
        const key = cat.categoryId || cat.id;
        initial.set(key, {
          categoryId: cat.categoryId || cat.id,
          categoryName: cat.categoryName,
          baseType: cat.baseType,
          plannedAmount: cat.plannedAmount,
          notes: cat.notes,
        });
      });
      setEditedCategories(initial);
      setHasChanges(false);
    }
  };

  // Enter edit mode
  const handleEnterEditMode = () => {
    setIsEditMode(true);
    setSearchParams({ edit: 'true' });
  };

  // Exit edit mode (cancel)
  const handleExitEditMode = () => {
    handleDiscardChanges();
    setIsEditMode(false);
    setSearchParams({});
  };

  // Compute displayed categories with edited values
  const displayedCategories = useMemo((): BudgetCategory[] => {
    return Array.from(editedCategories.values()).map((cat, index) => ({
      id: `edit-${index}`,
      categoryId: cat.categoryId,
      categoryName: cat.categoryName,
      baseType: cat.baseType,
      plannedAmount: cat.plannedAmount,
      notes: cat.notes,
    }));
  }, [editedCategories]);

  // Compute totals from edited values
  const editedTotals = useMemo(() => {
    let totalIncome = 0;
    let totalExpenses = 0;
    displayedCategories.forEach((cat) => {
      if (cat.baseType === 'credit') {
        totalIncome += cat.plannedAmount;
      } else {
        totalExpenses += cat.plannedAmount;
      }
    });
    return {
      totalIncome,
      totalExpenses,
      netResult: totalIncome - totalExpenses,
    };
  }, [displayedCategories]);

  // Get level 1 categories only for adding
  const level1Categories = useMemo(() => {
    return flatCategories.filter((c) => !c.parentId);
  }, [flatCategories]);

  const handleUpdate = async (data: UpdateBudgetRequest) => {
    if (!id) return;
    const result = await updateBudget(id, data);
    if (result) {
      setIsEditOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const success = await deleteBudget(id);
    if (success) {
      navigate('/budgets');
    }
  };

  const handleDuplicate = async (data: DuplicateBudgetRequest) => {
    if (!id) return;
    const result = await duplicateBudget(id, data);
    if (result) {
      setIsDuplicateOpen(false);
      navigate(`/budgets/${result.id}`);
    }
  };

  const handleValidate = async () => {
    if (!id || !currentBudget) return;
    setIsValidating(true);
    await updateBudget(id, {
      status: currentBudget.status === 'validated' ? 'draft' : 'validated',
      changeReason: currentBudget.status === 'validated' ? 'Passage en brouillon' : 'Validation du budget',
    });
    setIsValidating(false);
  };

  const handleExportPdf = async () => {
    if (!id) return;
    const blob = await exportPdf(id);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget_${currentBudget?.name || 'export'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleRestoreVersion = async (version: BudgetVersion) => {
    if (!id) return;
    const result = await restoreVersion(id, version.version);
    if (result) {
      setRestoreConfirmVersion(null);
      setIsVersionsOpen(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const activeAccounts = accounts.filter((a) => !a.isArchived);

  if (isLoadingDetail) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentBudget) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">{t('budgets.notFound')}</p>
        <Link
          to="/budgets"
          className="mt-4 inline-block text-violet-600 hover:text-violet-700"
        >
          ← {t('common.back')}
        </Link>
      </div>
    );
  }

  const isPositive = editedTotals.netResult >= 0;
  const isValidated = currentBudget.status === 'validated';

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <Link
            to="/budgets"
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 mb-2 inline-block"
          >
            ← {t('budgets.backToList')}
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
              {currentBudget.name}
            </h1>
            <span
              className={cn(
                'px-2 py-1 text-xs font-medium rounded-full',
                isValidated
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
              )}
            >
              {isValidated ? t('budgets.validated') : t('budgets.draft')}
            </span>
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
              v{currentBudget.version}
            </span>
          </div>
          {currentBudget.description && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {currentBudget.description}
            </p>
          )}
        </div>
        
        {/* Action buttons - responsive grid */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Buttons shown only in view mode */}
          {!isEditMode && (
            <>
              {/* Edit mode button - only show if not validated AND user has write permission */}
              {!isValidated && canManageBudgets && (
                <button
                  onClick={handleEnterEditMode}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
                >
                  <EditIcon className="w-4 h-4" />
                  <span className="hidden xs:inline">{t('common.edit')}</span>
                </button>
              )}
              {/* Export PDF button */}
              <button
                onClick={handleExportPdf}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded-lg transition-colors"
                title={t('budgets.exportPdf')}
              >
                <DownloadIcon className="w-4 h-4" />
                <span className="hidden sm:inline">{t('budgets.exportPdf')}</span>
              </button>
              {/* Validate/Invalidate button - only if user has write permission */}
              {canManageBudgets && (
                <button
                  onClick={handleValidate}
                  disabled={isValidating}
                  className={cn(
                    'flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50',
                    isValidated
                      ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                      : 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50'
                  )}
                  title={isValidated ? t('budgets.backToDraft') : t('budgets.validate')}
                >
                  {isValidated ? (
                    <>
                      <DraftIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('budgets.backToDraft')}</span>
                    </>
                  ) : (
                    <>
                      <ValidateIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('budgets.validate')}</span>
                    </>
                  )}
                </button>
              )}
              {/* More actions: history, duplicate, settings, delete */}
              <button
                onClick={() => setIsVersionsOpen(true)}
                className="p-2 text-slate-500 hover:text-violet-600 dark:text-slate-400 dark:hover:text-violet-400 transition-colors"
                title={t('budgets.versionHistory')}
              >
                <HistoryIcon className="w-5 h-5" />
              </button>
              {canManageBudgets && (
                <button
                  onClick={() => setIsDuplicateOpen(true)}
                  className="p-2 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors"
                  title={t('budgets.duplicate')}
                >
                  <CopyIcon className="w-5 h-5" />
                </button>
              )}
              {canManageBudgets && (
                <button
                  onClick={() => setIsEditOpen(true)}
                  className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
                  title={t('budgets.editMetadata')}
                >
                  <SettingsIcon className="w-5 h-5" />
                </button>
              )}
              {canManageBudgets && (
                <button
                  onClick={() => setIsDeleteOpen(true)}
                  className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title={t('common.delete')}
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              )}
            </>
          )}
          
          {/* Buttons shown only in edit mode */}
          {isEditMode && (
            <>
              <button
                onClick={handleExitEditMode}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveCategories}
                disabled={isSaving || !hasChanges}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? t('common.saving') : t('common.save')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button onClick={clearError} className="text-red-500 hover:text-red-700">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Edit mode banner */}
      {isEditMode && (
        <div className="p-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EditIcon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            <p className="text-violet-700 dark:text-violet-300 font-medium">
              {t('budgets.editModeActive')}
            </p>
          </div>
          {hasChanges && (
            <span className="text-sm text-violet-600 dark:text-violet-400">
              {t('budgets.unsavedChanges')}
            </span>
          )}
        </div>
      )}

      {/* Info card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <div>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('budgets.period')}</p>
            <p className="text-sm sm:text-base font-medium text-slate-900 dark:text-slate-100">
              {formatDate(currentBudget.startDate)} - {formatDate(currentBudget.endDate)}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('budgets.accounts')}</p>
            <p className="text-sm sm:text-base font-medium text-slate-900 dark:text-slate-100 truncate">
              {currentBudget.accounts.map((a) => a.accountName).join(', ')}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('budgets.source')}</p>
            <p className="text-sm sm:text-base font-medium text-slate-900 dark:text-slate-100">
              {currentBudget.sourceType === 'budget' && (
                <>
                  {t('budgets.fromBudget')}
                  {currentBudget.inflationRate && currentBudget.inflationRate > 0 && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                      (+{currentBudget.inflationRate}%)
                    </span>
                  )}
                </>
              )}
              {currentBudget.sourceType === 'statement' && (
                <>
                  {t('budgets.fromStatement')}
                  {currentBudget.inflationRate && currentBudget.inflationRate > 0 && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                      (+{currentBudget.inflationRate}%)
                    </span>
                  )}
                </>
              )}
              {(!currentBudget.sourceType || currentBudget.sourceType === 'manual') && t('budgets.manual')}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{t('budgets.createdAt')}</p>
            <p className="text-sm sm:text-base font-medium text-slate-900 dark:text-slate-100">
              {formatDate(currentBudget.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Categories tables - editable only in edit mode */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        <BudgetCategoryTable
          categories={displayedCategories}
          type="credit"
          total={editedTotals.totalIncome}
          title={t('budgets.plannedIncome')}
          editable={isEditMode}
          availableCategories={level1Categories}
          onAmountChange={handleAmountChange}
          onNotesChange={handleNotesChange}
          onAddCategory={handleAddCategory}
          onRemoveCategory={handleRemoveCategory}
        />
        <BudgetCategoryTable
          categories={displayedCategories}
          type="debit"
          total={editedTotals.totalExpenses}
          title={t('budgets.plannedExpenses')}
          editable={isEditMode}
          availableCategories={level1Categories}
          onAmountChange={handleAmountChange}
          onNotesChange={handleNotesChange}
          onAddCategory={handleAddCategory}
          onRemoveCategory={handleRemoveCategory}
        />
      </div>

      {/* Net result */}
      <div
        className={cn(
          'p-4 sm:p-6 rounded-xl border-2 text-center',
          isPositive
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        )}
      >
        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-1">
          {t('budgets.netResult')}
        </p>
        <p
          className={cn(
            'text-2xl sm:text-3xl font-bold',
            isPositive
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          )}
        >
          {isPositive ? '+' : ''}
          {formatAmount(editedTotals.netResult)}
        </p>
        <p className="text-xs sm:text-sm mt-2 text-slate-500 dark:text-slate-400">
          {isPositive ? t('budgets.surplus') : t('budgets.deficit')}
        </p>
      </div>

      {/* Edit Modal */}
      {isEditOpen && (
        <BudgetFormModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSubmit={async () => {}}
          onUpdate={handleUpdate}
          budget={currentBudget}
          accounts={activeAccounts}
          statements={statements}
          existingBudgets={budgets}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Duplicate Modal */}
      {isDuplicateOpen && (
        <BudgetDuplicateModal
          isOpen={isDuplicateOpen}
          onClose={() => setIsDuplicateOpen(false)}
          onSubmit={handleDuplicate}
          sourceBudgetName={currentBudget.name}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Version History Modal */}
      {isVersionsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsVersionsOpen(false)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('budgets.versionHistory')}
              </h3>
              <button
                onClick={() => setIsVersionsOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            
            {isLoadingVersions ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
              </div>
            ) : versionHistory.length === 0 ? (
              <p className="text-center py-8 text-slate-500 dark:text-slate-400">
                {t('budgets.noVersionHistory')}
              </p>
            ) : (
              <div className="overflow-y-auto flex-1">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white dark:bg-slate-800">
                    <tr className="text-sm text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      <th className="px-3 py-2 text-left font-medium">{t('budgets.version')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('budgets.date')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('budgets.changeReason')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('budgets.netResult')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {versionHistory.map((version) => (
                      <tr key={version.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="px-3 py-2">
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            v{version.version}
                          </span>
                          {version.version === currentBudget.version && (
                            <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                              ({t('budgets.current')})
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400">
                          {formatDateTime(version.createdAt)}
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400">
                          {version.changeReason || '-'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span
                            className={cn(
                              'font-medium',
                              version.snapshot.netResult >= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            )}
                          >
                            {formatAmount(version.snapshot.netResult)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {version.version !== currentBudget.version && canManageBudgets && (
                            <button
                              onClick={() => setRestoreConfirmVersion(version)}
                              className="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
                            >
                              {t('budgets.restore')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Restore Version Confirmation Modal */}
      {restoreConfirmVersion && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setRestoreConfirmVersion(null)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('budgets.restoreConfirmTitle')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t('budgets.restoreConfirmMessage', { version: restoreConfirmVersion.version })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRestoreConfirmVersion(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleRestoreVersion(restoreConfirmVersion)}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg disabled:opacity-50"
              >
                {t('budgets.restore')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsDeleteOpen(false)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('budgets.deleteConfirmTitle')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t('budgets.deleteConfirmMessage')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Icon components
const DownloadIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const EditIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const CopyIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

const ValidateIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const DraftIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const HistoryIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const SettingsIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

export default BudgetDetailPage;
